// ============================================================
// entradas.js — Reglas PURAS del widget de venta de entradas
// ============================================================
//
// Lo que el widget (`core/entradas-runtime.js`) decide sin preguntar al servidor: qué días del
// calendario se pueden pulsar, cuánto suma la selección, cuántas plazas ocupa y qué trae la URL al
// volver de Redsys. Son ESPEJOS de `web/lib/entradas.py` (la SSOT): el navegador solo los usa para
// no ofrecer lo que el servidor va a rechazar; la compra se revalida entera allí (`validar_compra`).
//
// Módulo PURO (sin DOM ni `window`) → node-testable (`tests/test_entradas_widget.py`).
//
// Las fechas viajan como 'AAAA-MM-DD' y se comparan como CADENA (orden lexicográfico = orden de
// fechas) o en UTC: con `new Date(y, m, d)` local, un cambio de hora de marzo movía el día.

/** 'AAAA-MM-DD' → número de días desde 1970 (UTC). NaN si no es una fecha. */
export function diaNum(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!m) return NaN;
    return Math.floor(Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000);
}

/** Número de días desde 1970 → 'AAAA-MM-DD'. */
export function isoDeNum(n) {
    const d = new Date(n * 86400000);
    const p = (x) => String(x).padStart(2, '0');
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}

/** Día de la semana como `weekday()` de Python (lunes 0 … domingo 6). */
export function diaSemana(iso) {
    const n = diaNum(iso);
    // 1970-01-01 fue jueves (weekday 3).
    return ((n + 3) % 7 + 7) % 7;
}

/**
 * ¿Se venden entradas ese día? Espejo de `entradas.fecha_vendible` + `dia_abierto`:
 * entre hoy y hoy + max_dias, día de la semana abierto y fuera de los cierres. Un cierre con un
 * solo extremo cierra solo ese día (la misma semántica que el formulario antiguo).
 * `cfg` = el `evento` de `GET /site_entradas/evento/`.
 */
export function diaVendible(iso, cfg) {
    const n = diaNum(iso);
    const hoy = diaNum(cfg && cfg.hoy);
    if (Number.isNaN(n) || Number.isNaN(hoy)) return false;
    if (n < hoy || n > hoy + (Number(cfg.max_dias) || 0)) return false;
    if ((cfg.dias_semana || []).indexOf(diaSemana(iso)) === -1) return false;
    for (const c of (cfg.cierres || [])) {
        const desde = c && c[0] ? c[0] : '';
        const hasta = c && c[1] ? c[1] : '';
        if (desde && hasta) {
            if (desde <= iso && iso <= hasta) return false;
        } else if (desde || hasta) {
            if (iso === (desde || hasta)) return false;
        }
    }
    return true;
}

/** Primer día vendible desde hoy, o '' si no hay ninguno en todo el horizonte. */
export function primerDiaVendible(cfg) {
    const hoy = diaNum(cfg && cfg.hoy);
    if (Number.isNaN(hoy)) return '';
    const max = Number(cfg.max_dias) || 0;
    for (let i = 0; i <= max; i++) {
        const iso = isoDeNum(hoy + i);
        if (diaVendible(iso, cfg)) return iso;
    }
    return '';
}

/**
 * Celdas de un mes para pintar el calendario, con la semana empezando en LUNES.
 * `anio`, `mes` (0-11). Devuelve [{iso|null, dia, vendible, hoy}], con null de relleno al
 * principio para cuadrar el primer día bajo su columna.
 */
export function celdasDelMes(anio, mes, cfg) {
    const primero = isoDeNum(Math.floor(Date.UTC(anio, mes, 1) / 86400000));
    const dias = new Date(Date.UTC(anio, mes + 1, 0)).getUTCDate();
    const celdas = [];
    for (let i = 0; i < diaSemana(primero); i++) celdas.push({ iso: null });
    const base = diaNum(primero);
    for (let d = 0; d < dias; d++) {
        const iso = isoDeNum(base + d);
        celdas.push({ iso, dia: d + 1, vendible: diaVendible(iso, cfg), hoy: iso === cfg.hoy });
    }
    return celdas;
}

/** ¿Se puede ir a ese mes? Solo los que caen entre hoy y hoy + max_dias. */
export function mesNavegable(anio, mes, cfg) {
    const hoy = diaNum(cfg && cfg.hoy);
    if (Number.isNaN(hoy)) return false;
    const ini = Math.floor(Date.UTC(anio, mes, 1) / 86400000);
    const fin = Math.floor(Date.UTC(anio, mes + 1, 0) / 86400000);
    return fin >= hoy && ini <= hoy + (Number(cfg.max_dias) || 0);
}

/** Copia PURA de `entradas.plazas_bloqueadas` (y esa, de `models.get_total_locked_slots`). */
export function plazasBloqueadas(total, minimo, porBloques) {
    const t = Math.trunc(Number(total) || 0);
    const m = Math.trunc(Number(minimo) || 0);
    if (m > 1) {
        if (t < m) return m;
        if (porBloques) return m * Math.ceil(t / m);
    }
    return t;
}

/**
 * Líneas de la selección, en el orden del evento: cada tipo y, debajo, su vinculado SOLO si el
 * principal lleva alguna (el servidor rechaza un vinculado suelto, `normalizar_seleccion`).
 * `cantidades` = { product_id: n }. Devuelve [{product_id, nombre, precio, cupo, cantidad, vinculado}].
 */
export function lineasDe(cfg, cantidades) {
    const out = [];
    const vistos = new Set();
    for (const t of ((cfg && cfg.tipos) || [])) {
        const n = Math.trunc(Number(cantidades[t.product_id]) || 0);
        if (n > 0 && !vistos.has(t.product_id)) {
            vistos.add(t.product_id);
            out.push({ ...t, cantidad: n, vinculado: false });
        }
        const v = t.vinculado;
        if (v && n > 0 && !vistos.has(v.product_id)) {
            const nv = Math.trunc(Number(cantidades[v.product_id]) || 0);
            if (nv > 0) {
                vistos.add(v.product_id);
                out.push({ ...v, cantidad: nv, vinculado: true });
            }
        }
    }
    return out;
}

/**
 * La config con los precios DE LA SESIÓN elegida (precios especiales por fechas o franjas, Fase 3 del plan de
 * entradas): `sesion.precios` = { product_id: "12.10" } lo calcula el servidor (`entradas.precio`). Sin sesión, o
 * sin precio para un tipo, se queda el de la config. Devuelve un objeto NUEVO.
 */
export function cfgDeSesion(cfg, sesion) {
    const precios = (sesion && sesion.precios) || null;
    if (!cfg || !precios) return cfg;
    const conPrecio = (t) => (Object.prototype.hasOwnProperty.call(precios, String(t.product_id))
        ? Object.assign({}, t, { precio: precios[String(t.product_id)] }) : t);
    return Object.assign({}, cfg, {
        tipos: (cfg.tipos || []).map((t) => Object.assign(conPrecio(t), t.vinculado ? { vinculado: conPrecio(t.vinculado) } : {})),
    });
}

/** Total en CÉNTIMOS (entero: sumar decimales en coma flotante descuadra el céntimo). */
export function totalCentimos(lineas) {
    return lineas.reduce((s, l) => s + Math.round(Number(l.precio) * 100) * l.cantidad, 0);
}

/** Plazas reales y plazas que bloquea la compra (mínimo y bloques del evento). */
export function plazasDe(cfg, lineas) {
    const plazas = lineas.reduce((s, l) => s + l.cantidad * (Math.trunc(Number(l.cupo)) || 0), 0);
    return { plazas, bloqueadas: plazasBloqueadas(plazas, cfg.minimo, cfg.por_bloques) };
}

/** Lo que espera `POST /site_entradas/comprar/` en `items`: [{id, cantidad}]. */
export function itemsDe(lineas) {
    return lineas.map((l) => ({ id: l.product_id, cantidad: l.cantidad }));
}

/**
 * Cantidad de un tipo con el tope del evento (M8): `max_por_tipo` por tipo y `max_total` entre todos (null = sin
 * tope). Espejo de `entradas.normalizar_seleccion(max_total)`: el servidor rechaza una compra que lo pase.
 */
export function cantidadPermitida(cfg, cantidades, productId, n) {
    const maxTipo = Number(cfg && cfg.max_por_tipo) || 99;
    let tope = Math.min(maxTipo, Math.max(0, Math.trunc(Number(n) || 0)));
    const maxTotal = Number(cfg && cfg.max_total) || 0;
    if (maxTotal > 0) {
        let otros = 0;
        Object.keys(cantidades || {}).forEach((k) => { if (String(k) !== String(productId)) otros += Math.trunc(Number(cantidades[k]) || 0); });
        tope = Math.min(tope, Math.max(0, maxTotal - otros));
    }
    return tope;
}

/**
 * Al poner a 0 un principal, su vinculado también se va a 0: el visitante deja de verlo y
 * mandarlo después lo convertiría en un vinculado suelto (400 en el servidor). Devuelve un objeto
 * NUEVO con las cantidades.
 */
export function ajustarVinculados(cfg, cantidades) {
    const out = { ...cantidades };
    for (const t of ((cfg && cfg.tipos) || [])) {
        if (t.vinculado && !(Number(out[t.product_id]) > 0)) out[t.vinculado.product_id] = 0;
    }
    return out;
}

/** Validación de contacto: espejo de `entradas.contacto_valido` (los tres obligatorios). */
export function contactoValido(nombre, telefono, email) {
    const n = String(nombre || '').trim();
    const t = String(telefono || '').trim();
    const e = String(email || '').trim();
    if (!n || n.length > 100) return false;
    if (!t || t.length > 100 || t.replace(/\D/g, '').length < 6) return false;
    if (!e || e.length > 255 || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return false;
    return true;
}

/**
 * Lo que trae la URL al volver del banco: la plataforma de cobro (web/medios_cobro, vista `volver`) devuelve
 * al visitante a ESTA página con `?n3i_tx=<uuid de la transacción>`. Volver no dice si se cobró: eso lo
 * contesta `/medios_cobro/estado/<uuid>/`. Lo que no sea un uuid se descarta antes de mandarlo a ningún sitio.
 */
export function leerVueltaPago(search) {
    let q;
    try { q = new URLSearchParams(search || ''); } catch (_) { return null; }
    const tx = (q.get('n3i_tx') || '').toLowerCase();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(tx)) return null;
    // `n3i_cancelado=1`: el comprador canceló en la pasarela (Stripe, PayPal). La pone la vista `volver` solo si la
    // transacción no tiene dinero; aun así manda el estado del servidor (ver `atenderVueltaPago`).
    return { tx: tx, cancelado: q.get('n3i_cancelado') === '1' };
}

/** La misma URL sin el parámetro del pago (para que recargar no repita la consulta). */
export function quitarVueltaPago(search) {
    let q;
    try { q = new URLSearchParams(search || ''); } catch (_) { return ''; }
    q.delete('n3i_tx');
    q.delete('n3i_cancelado');
    const s = q.toString();
    return s ? '?' + s : '';
}

/**
 * Qué hacer con la respuesta de `/medios_cobro/estado/<tx>/`: 'ok' (cobrada y con la venta creada: hay
 * reserva que enseñar), 'error' (no se cobró, caducó o se devolvió) o 'esperar' (en el banco, o cobrada y
 * la venta aún creándose). `null` = sin respuesta (red, cupo): se sigue preguntando.
 */
export function veredictoPago(r) {
    if (!r) return 'esperar';
    if (!r.ok) return r.error === 'rate_limited' ? 'esperar' : 'error';
    if (r.materializada && r.resultado && r.resultado.reserva) return 'ok';
    if (['rechazada', 'caducada', 'devuelta', 'devuelta_parcial'].indexOf(r.estado) >= 0) return 'error';
    return 'esperar';
}

/**
 * Clave de etiqueta para cada código de error del servidor (`entradas.ERR_*` y los de la frontera
 * HTTP). Lo desconocido cae en el genérico: nunca se enseña el código crudo.
 */
export const ERRORES = {
    evento_no_disponible: 'ne_err_evento',
    fecha_no_disponible: 'ne_err_fecha',
    hora_no_disponible: 'ne_err_hora',
    sin_entradas: 'ne_err_entradas',
    entrada_no_valida: 'ne_err_entradas',
    aforo_completo: 'ne_err_aforo',
    datos_contacto: 'ne_err_datos',
    pago_no_configurado: 'ne_err_pago',
    rate_limited: 'ne_err_rate',
    en_curso: 'ne_err_en_curso',
    precio_cambiado: 'ne_err_precio',
    agente_no_valido: 'ne_err_agente',
};

export function claveDeError(codigo) {
    return ERRORES[codigo] || 'ne_err_red';
}
